// src/pages/HomePage.tsx
import React from 'react'
import { useNavigate } from 'react-router-dom'
import { useSelector } from 'react-redux'
import { Card, Space, Typography, Button, Row, Col, Tooltip } from 'antd'
import {
  VideoCameraOutlined,
  AudioOutlined,
  BookOutlined,
  UserOutlined,
  HeartOutlined,
  LineChartOutlined,
  UploadOutlined,
  PlusOutlined,
  RiseOutlined,
  TeamOutlined,
} from '@ant-design/icons'
import { Breadcrumbs } from '../../components'
import { selectAllVideos } from '../../redux/features/videos/videoSelectors'
import { selectAllAudios } from '../../redux/features/audio/audioSelector'
import { selectAllBooks } from '../../redux/features/books/bookSelector'
import { selectAllUsers } from '../../redux/features/user/userSelectors'

const { Text, Title } = Typography

const HomePage: React.FC = () => {
  const navigate = useNavigate()

  // Get actual data from Redux store
  const videos = useSelector(selectAllVideos)
  const audios = useSelector(selectAllAudios)
  const books = useSelector(selectAllBooks)
  const users = useSelector(selectAllUsers)

  // Calculate stats
  const totalLikes =
    videos.reduce((sum, v) => sum + (v.likes || 0), 0) +
    audios.reduce((sum, a) => sum + (a.likes || 0), 0) +
    books.reduce((sum, b) => sum + (b.likes || 0), 0)

  const subscribedUsers = (users || []).filter((u: any) => u.isSubscribed).length

  // Get recent content (last 5)
  const recentContent = [
    ...videos.map((v) => ({
      type: 'video',
      title: v.title,
      icon: <VideoCameraOutlined style={{ color: '#1890ff' }} />,
    })),
    ...audios.map((a) => ({
      type: 'audio',
      title: a.title,
      icon: <AudioOutlined style={{ color: '#722ed1' }} />,
    })),
    ...books.map((b) => ({
      type: 'book',
      title: b.title,
      icon: <BookOutlined style={{ color: '#389e0d' }} />,
    })),
  ]
    .slice(0, 5)

  // Card styles
  const cardStyle = {
    borderRadius: '12px',
    boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
    border: 'none',
    height: '100%',
  }

  const gradientCards = {
    video: 'linear-gradient(135deg, #e6f7ff 0%, #bae7ff 100%)',
    audio: 'linear-gradient(135deg, #f9f0ff 0%, #efdbff 100%)',
    book: 'linear-gradient(135deg, #f6ffed 0%, #d9f7be 100%)',
    user: 'linear-gradient(135deg, #fff7e6 0%, #ffe7ba 100%)',
  }

  return (
    <>
      <Breadcrumbs />

      <div style={{ padding: '20px' }}>
        {/* Quick Actions */}
        <div
          style={{
            marginBottom: '24px',
            padding: '20px',
            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
            borderRadius: '12px',
            color: '#fff',
          }}
        >
          <div
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              flexWrap: 'wrap',
              gap: '16px',
            }}
          >
            <div>
              <Title level={4} style={{ margin: 0, color: '#fff' }}>
                Quick Actions
              </Title>
              <Text style={{ color: 'rgba(255,255,255,0.8)' }}>
                Upload new content with one click
              </Text>
            </div>
            <Space wrap>
              <Button
                type="primary"
                icon={<VideoCameraOutlined />}
                onClick={() => navigate('/videos')}
                style={{
                  background: 'rgba(255,255,255,0.2)',
                  border: '1px solid rgba(255,255,255,0.3)',
                }}
              >
                Upload Video
              </Button>
              <Button
                type="primary"
                icon={<AudioOutlined />}
                onClick={() => navigate('/audios')}
                style={{
                  background: 'rgba(255,255,255,0.2)',
                  border: '1px solid rgba(255,255,255,0.3)',
                }}
              >
                Upload Audio
              </Button>
              <Button
                type="primary"
                icon={<BookOutlined />}
                onClick={() => navigate('/books')}
                style={{
                  background: 'rgba(255,255,255,0.2)',
                  border: '1px solid rgba(255,255,255,0.3)',
                }}
              >
                Upload Book
              </Button>
            </Space>
          </div>
        </div>

        {/* Content Overview */}
        <Title level={4} style={{ marginBottom: '16px' }}>
          Content Overview
        </Title>
        <Row gutter={[16, 16]} style={{ marginBottom: '24px' }}>
          <Col xs={24} sm={12} lg={6}>
            <Card
              style={{ ...cardStyle, background: gradientCards.video }}
              hoverable
              onClick={() => navigate('/videos')}
            >
              <div style={{ textAlign: 'center', padding: '16px 0' }}>
                <div
                  style={{
                    width: '64px',
                    height: '64px',
                    borderRadius: '50%',
                    background: 'rgba(24, 144, 255, 0.1)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    margin: '0 auto 12px',
                  }}
                >
                  <VideoCameraOutlined style={{ fontSize: '32px', color: '#1890ff' }} />
                </div>
                <Title level={3} style={{ margin: 0, color: '#1890ff' }}>
                  {videos.length}
                </Title>
                <Text strong>Videos</Text>
              </div>
            </Card>
          </Col>

          <Col xs={24} sm={12} lg={6}>
            <Card
              style={{ ...cardStyle, background: gradientCards.audio }}
              hoverable
              onClick={() => navigate('/audios')}
            >
              <div style={{ textAlign: 'center', padding: '16px 0' }}>
                <div
                  style={{
                    width: '64px',
                    height: '64px',
                    borderRadius: '50%',
                    background: 'rgba(114, 46, 209, 0.1)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    margin: '0 auto 12px',
                  }}
                >
                  <AudioOutlined style={{ fontSize: '32px', color: '#722ed1' }} />
                </div>
                <Title level={3} style={{ margin: 0, color: '#722ed1' }}>
                  {audios.length}
                </Title>
                <Text strong>Audio Files</Text>
              </div>
            </Card>
          </Col>

          <Col xs={24} sm={12} lg={6}>
            <Card
              style={{ ...cardStyle, background: gradientCards.book }}
              hoverable
              onClick={() => navigate('/books')}
            >
              <div style={{ textAlign: 'center', padding: '16px 0' }}>
                <div
                  style={{
                    width: '64px',
                    height: '64px',
                    borderRadius: '50%',
                    background: 'rgba(56, 158, 13, 0.1)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    margin: '0 auto 12px',
                  }}
                >
                  <BookOutlined style={{ fontSize: '32px', color: '#389e0d' }} />
                </div>
                <Title level={3} style={{ margin: 0, color: '#389e0d' }}>
                  {books.length}
                </Title>
                <Text strong>Books</Text>
              </div>
            </Card>
          </Col>

          <Col xs={24} sm={12} lg={6}>
            <Card
              style={{ ...cardStyle, background: gradientCards.user }}
              hoverable
              onClick={() => navigate('/users')}
            >
              <div style={{ textAlign: 'center', padding: '16px 0' }}>
                <div
                  style={{
                    width: '64px',
                    height: '64px',
                    borderRadius: '50%',
                    background: 'rgba(250, 140, 22, 0.1)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    margin: '0 auto 12px',
                  }}
                >
                  <TeamOutlined style={{ fontSize: '32px', color: '#fa8c16' }} />
                </div>
                <Title level={3} style={{ margin: 0, color: '#fa8c16' }}>
                  {(users || []).length}
                </Title>
                <Text strong>Users</Text>
              </div>
            </Card>
          </Col>
        </Row>

        {/* Quick Stats */}
        <Title level={4} style={{ marginBottom: '16px' }}>
          Quick Stats
        </Title>
        <Row gutter={[16, 16]} style={{ marginBottom: '24px' }}>
          <Col xs={24} sm={12} lg={6}>
            <Card style={cardStyle}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
                <div
                  style={{
                    width: '48px',
                    height: '48px',
                    borderRadius: '12px',
                    background: 'linear-gradient(135deg, #1890ff 0%, #69c0ff 100%)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  <RiseOutlined style={{ fontSize: '24px', color: '#fff' }} />
                </div>
                <div>
                  <Text type="secondary" style={{ fontSize: '12px' }}>
                    TOTAL CONTENT
                  </Text>
                  <Title level={4} style={{ margin: 0 }}>
                    {videos.length + audios.length + books.length}
                  </Title>
                </div>
              </div>
            </Card>
          </Col>

          <Col xs={24} sm={12} lg={6}>
            <Card style={cardStyle}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
                <div
                  style={{
                    width: '48px',
                    height: '48px',
                    borderRadius: '12px',
                    background: 'linear-gradient(135deg, #ff4d4f 0%, #ff7875 100%)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  <HeartOutlined style={{ fontSize: '24px', color: '#fff' }} />
                </div>
                <div>
                  <Text type="secondary" style={{ fontSize: '12px' }}>
                    TOTAL LIKES
                  </Text>
                  <Title level={4} style={{ margin: 0 }}>
                    {totalLikes.toLocaleString()}
                  </Title>
                </div>
              </div>
            </Card>
          </Col>

          <Col xs={24} sm={12} lg={6}>
            <Card style={cardStyle}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
                <div
                  style={{
                    width: '48px',
                    height: '48px',
                    borderRadius: '12px',
                    background: 'linear-gradient(135deg, #52c41a 0%, #95de64 100%)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  <UserOutlined style={{ fontSize: '24px', color: '#fff' }} />
                </div>
                <div>
                  <Text type="secondary" style={{ fontSize: '12px' }}>
                    SUBSCRIBED USERS
                  </Text>
                  <Title level={4} style={{ margin: 0 }}>
                    {subscribedUsers}
                  </Title>
                </div>
              </div>
            </Card>
          </Col>

          <Col xs={24} sm={12} lg={6}>
            <Card
              style={cardStyle}
              hoverable
              onClick={() => navigate('/analytics')}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
                <div
                  style={{
                    width: '48px',
                    height: '48px',
                    borderRadius: '12px',
                    background: 'linear-gradient(135deg, #722ed1 0%, #b37feb 100%)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  <LineChartOutlined style={{ fontSize: '24px', color: '#fff' }} />
                </div>
                <div>
                  <Text type="secondary" style={{ fontSize: '12px' }}>
                    ANALYTICS
                  </Text>
                  <Title level={4} style={{ margin: 0 }}>
                    View Stats
                  </Title>
                </div>
              </div>
            </Card>
          </Col>
        </Row>

        {/* Recent Content */}
        {recentContent.length > 0 && (
          <>
            <Title level={4} style={{ marginBottom: '16px' }}>
              Recent Content
            </Title>
            <Card style={cardStyle}>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                {recentContent.map((item, index) => (
                  <div
                    key={index}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '12px',
                      padding: '8px 12px',
                      background: '#fafafa',
                      borderRadius: '8px',
                    }}
                  >
                    {item.icon}
                    <Text strong style={{ flex: 1 }}>
                      {item.title}
                    </Text>
                    <Text type="secondary" style={{ fontSize: '12px', textTransform: 'capitalize' }}>
                      {item.type}
                    </Text>
                  </div>
                ))}
              </div>
            </Card>
          </>
        )}
      </div>
    </>
  )
}

export default HomePage
